import type { AuthProvider } from "@refinedev/core"
import { signIn, signOut } from "next-auth/react"

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    // 关键:NextAuth v5 的 result.ok 仅是 HTTP response.ok,跟登录成功无关。
    // 真正判断登录是否成功要看 result.error 是否为 undefined。
    // 错误密码会触发 HTTP 200 + error="CredentialsSignin",result.ok 仍是 true,需以 error 字段为准。
    try {
      const result = await signIn("credentials", { email, password, redirect: false })
      if (result && !(result as { error?: string }).error) {
        return { success: true, redirectTo: "/dashboard" }
      }
    } catch {
      // CredentialsSignin / 网络错都归为失败
    }
    return { success: false, error: { name: "登录失败", message: "邮箱或密码错误" } }
  },

  logout: async () => {
    try {
      const result = await signOut({ redirect: false, redirectTo: "/login" })
      if (!result?.url || new URL(result.url, window.location.origin).searchParams.has("error")) {
        return { success: false, error: { name: "退出失败", message: "退出未完成，请重试。" } }
      }
      const response = await fetch("/api/auth/session", { cache: "no-store" })
      if (!response.ok) {
        return { success: false, error: { name: "退出失败", message: "无法确认退出状态，请稍后重试。" } }
      }
      const session = await response.json()
      if (session?.user) {
        return { success: false, error: { name: "退出失败", message: "登录状态尚未清除，请重试。" } }
      }
      return { success: true, redirectTo: "/login" }
    } catch {
      return { success: false, error: { name: "退出失败", message: "暂时无法退出登录，请稍后重试。" } }
    }
  },

  check: async () => {
    const res = await fetch("/api/auth/session")
    const session = await res.json()
    if (session?.user) return { authenticated: true }
    return { authenticated: false, redirectTo: "/login" }
  },

  getIdentity: async () => {
    const res = await fetch("/api/auth/session")
    const session = await res.json()
    if (!session?.user) return null
    const profile = await fetch("/api/profile", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .catch(() => null)
    return {
      id: session.user.id ?? session.user.email,
      name: session.user.name,
      email: session.user.email,
      role: session.user.role,
      avatar: session.user.name?.charAt(0),
      avatarUrl: profile?.avatarUrl ?? null,
      gender: profile?.gender ?? null,
    }
  },

  onError: async (error) => {
    // 不主动调 logout 清 cookie。dev HMR 期间个别 API 路由偶发 401,如果在这里 return { logout: true },
    // Refine 会自动 signOut 把 cookie 清光,导致"改一次代码就要重新输密码"。
    // 真过期由 check 函数兜底——它跳 /login 但不清 cookie,刷新或重新登录页直接成功。
    return { error }
  },
}
